/**
 * Helper WINDX-036 (ADR-028, contrato v7.1).
 *
 * Processa `option.series[]` do chart-data SH e identifica series cujo
 * `data_extensao_efetiva` ficou em fallback retroativo (rodada D-N em vez
 * de D+0). Retorna:
 *   - `option`: clone de `option` com `itemStyle.opacity = 0.7` aplicado
 *     em series com defasagem >= 2 dias (severidade 'aviso')
 *   - `badges`: lista de descritores para o FE renderizar acima do grafico,
 *     deduplicados por `(id_modelo, dataExtensao)` — ver bug PO 2026-05-27.
 *
 * Severidade escalonada conforme spec:
 *   - 0 dias = sincrona, nao incluida nos badges
 *   - 1 dia  = 'sutil' (badge cinza simples)
 *   - >= 2   = 'aviso' (badge amarelo + opacity 0.7 na serie)
 *
 * Bug PO 2026-05-27: com muitas usinas selecionadas (ex.: 15), badges
 * acumulavam 1 por (modelo × usina) pois cada serie ECharts virava badge.
 * Como `data_extensao_efetiva` eh atributo da RODADA do modelo (nao da
 * entidade), todas as series de WRF+GEFS35 herdam a mesma data. Solucao:
 * dedup por `(id_modelo, dataExtensao)` no momento do push do badge.
 * Opacity das series NAO eh deduplicada — continua sendo aplicada em
 * cada serie individualmente para preservar a semantica visual da curva.
 */
import type { EChartsOption } from 'helper/windx';

export interface ExtensaoBadge {
  /** Nome da serie em `option.series[i].name` */
  nome: string;
  /** Data efetiva da rodada de extensao (ISO 8601, do BE). */
  dataExtensao: string;
  /** Quantos dias antes da rodada atual (1, 2, 3, ...). */
  defasagemDias: number;
  /** 'sutil' = 1 dia; 'aviso' = >=2 dias. */
  severidade: 'sutil' | 'aviso';
  /** Formato 'DD/MM' para tooltip/badge. */
  dataExtensaoFmt: string;
}

interface SerieComExtensao {
  name?: string;
  id_modelo?: number;
  data_extensao_efetiva?: string | null;
  itemStyle?: Record<string, unknown>;
  [key: string]: unknown;
}

/**
 * Calcula a diferenca em dias entre `dataRodada` e `dataExtensao`.
 * Ambas em formato ISO 'YYYY-MM-DD'. Resultado positivo quando
 * dataExtensao < dataRodada (fallback retroativo).
 */
function diferencaDiasISO(dataRodada: string, dataExtensao: string): number {
  // Date(ISO) interpreta como UTC; usar substring evita drift de fuso.
  const a = new Date(`${dataRodada}T00:00:00Z`).getTime();
  const b = new Date(`${dataExtensao}T00:00:00Z`).getTime();
  return Math.round((a - b) / (1000 * 60 * 60 * 24));
}

function formatarDDMM(iso: string): string {
  // iso = 'YYYY-MM-DD'
  const [, mes, dia] = iso.split('-');
  return `${dia}/${mes}`;
}

/**
 * Extrai o nome do modelo a partir do `name` da serie ECharts.
 *
 * Convencao do BE chart-data SH: `{modelo} - {entidade}` (ex.:
 * "WRF + GEFS35 - VENTOS DE SANTO ANTONIO 04"). Em casos onde nao ha
 * separador (serie agregada ou modelo unico), retorna o `name` inteiro
 * defensivamente.
 *
 * Bug PO 2026-05-27: badges agregados precisam exibir so o nome do modelo,
 * nao o sufixo da entidade.
 */
function extractModeloFromName(name: string): string {
  const idx = name.indexOf(' - ');
  return idx === -1 ? name : name.substring(0, idx);
}

export interface DerivarExtensaoOutput {
  option: EChartsOption;
  badges: ExtensaoBadge[];
}

/**
 * Processa o `option` ECharts retornado pelo BE (WINDX-036).
 *
 * @param option Objeto ECharts do response.
 * @param dataRodada Data nominal da rodada (YYYY-MM-DD). Em geral o
 *   request envia `data_simulacao` ou backend usa CURRENT_DATE — o caller
 *   pode passar `new Date().toISOString().slice(0,10)` quando nao tiver
 *   `data_simulacao`.
 */
export function derivarExtensaoRetroativa(
  option: EChartsOption,
  dataRodada: string,
): DerivarExtensaoOutput {
  const rawSeries = (option.series as SerieComExtensao[] | undefined) ?? [];

  const badges: ExtensaoBadge[] = [];
  // Dedup por (id_modelo, dataExtensao): com 15 usinas × 1 modelo, todas
  // as series herdam a mesma data_extensao_efetiva da rodada do modelo.
  // Sem dedup, 15 badges iguais poluem o topo do grafico (bug PO 2026-05-27).
  // Fallback para `name` quando id_modelo ausente (resposta legacy/edge).
  const seenBadgeKeys = new Set<string>();

  // Clona series alterando itemStyle apenas onde necessario. Mantemos
  // referencia a outras propriedades para nao explodir o objeto ECharts
  // (campos opcionais como markLine, areaStyle etc).
  const novasSeries = rawSeries.map((serie) => {
    const data = serie.data_extensao_efetiva;
    if (!data || !serie.name) return serie;

    const defasagem = diferencaDiasISO(dataRodada, data);
    if (defasagem <= 0) return serie;

    const severidade: ExtensaoBadge['severidade'] = defasagem >= 2 ? 'aviso' : 'sutil';

    const badgeKey = `${serie.id_modelo ?? serie.name}|${data}`;
    if (!seenBadgeKeys.has(badgeKey)) {
      seenBadgeKeys.add(badgeKey);
      badges.push({
        nome: extractModeloFromName(serie.name),
        dataExtensao: data,
        dataExtensaoFmt: formatarDDMM(data),
        defasagemDias: defasagem,
        severidade,
      });
    }

    // Opacity aplicada por serie (NAO deduplicada): cada curva ECharts
    // com defasagem >= 2 fica com opacity 0.7 individualmente, preservando
    // a semantica visual original.
    if (severidade !== 'aviso') return serie;

    const itemStyle = { ...(serie.itemStyle ?? {}), opacity: 0.7 };
    return { ...serie, itemStyle };
  });

  return {
    option: { ...option, series: novasSeries },
    badges,
  };
}
